import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cashierFormSchema, type CashierFormInput } from '../routes/cashiers.schema';
import type { UsersApiError } from '../api/users';

interface StoreOption { id: string; name: string }

/**
 * Add-cashier dialog. Submits a CreateUserRequest via the parent's onSubmit
 * callback; the parent owns the mutation so it can manage cache invalidation.
 * Server-side errors (notably 409 DUPLICATE_EMAIL) are surfaced via
 * `serverError` so the caller can re-render the dialog with a friendly hint.
 */
export function CashierFormDialog({
  open, onClose, onSubmit, stores, serverError,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (d: CashierFormInput) => void;
  stores: StoreOption[];
  serverError?: UsersApiError | null;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<CashierFormInput>({
    resolver: zodResolver(cashierFormSchema),
    defaultValues: { role: 'CASHIER' },
  });

  // Local override so the prop-driven server error can be dismissed when the
  // user starts editing again — otherwise it'd stick around forever.
  const [dismissed, setDismissed] = useState(false);
  const visibleServerError = !dismissed && serverError ? serverError : null;

  if (!open) return null;
  const friendly =
    visibleServerError?.code === 'DUPLICATE_EMAIL'
      ? 'A cashier with that email already exists in this merchant.'
      : visibleServerError?.detail ?? visibleServerError?.message;

  return (
    <div role="dialog" aria-modal="true" aria-label="Add cashier" className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <form
        onSubmit={handleSubmit(onSubmit)}
        onChange={() => setDismissed(true)}
        className="bg-white rounded p-6 w-[420px] space-y-3"
      >
        <h2 className="text-lg font-bold text-primary">Add cashier</h2>
        <label className="block text-sm">
          Email
          <input
            type="email"
            autoComplete="off"
            {...register('email')}
            className="mt-1 w-full border rounded p-2"
          />
        </label>
        {errors.email && <p className="text-sm text-accent">{errors.email.message}</p>}

        <label className="block text-sm">
          Display name
          <input {...register('displayName')} className="mt-1 w-full border rounded p-2" />
        </label>
        {errors.displayName && <p className="text-sm text-accent">{errors.displayName.message}</p>}

        <label className="block text-sm">
          Role
          <select {...register('role')} className="mt-1 w-full border rounded p-2">
            <option value="CASHIER">Cashier</option>
            <option value="MANAGER">Manager</option>
          </select>
        </label>

        <label className="block text-sm">
          Store
          <select {...register('storeId')} className="mt-1 w-full border rounded p-2">
            <option value="">— select a store —</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        {errors.storeId && <p className="text-sm text-accent">{errors.storeId.message}</p>}

        {friendly && (
          <p role="alert" className="text-sm text-accent">{friendly}</p>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" className="rounded bg-primary px-4 py-2 text-white">Create cashier</button>
        </div>
      </form>
    </div>
  );
}
